import React, { useId, useMemo, useRef } from "react";
import Button from "../../ui/Button";
import DrawerA11yWrapper from "../../ui/DrawerA11yWrapper";
import StatusBadge from "../../ui/StatusBadge";
import { toneTokens } from "../../ui/statusTokens";
import { colors, radius, shadows, spacing, typography } from "../../ui/tokens";

const drawerStyle = {
  width: "min(92vw, 620px)",
  background: colors.surface,
  boxShadow: shadows.lg,
};

const drawerLayoutStyle = {
  display: "grid",
  gridTemplateRows: "auto 1fr",
  height: "100%",
};

const headerStyle = {
  display: "grid",
  gap: spacing.md,
  padding: spacing.xl,
  borderBottom: `1px solid ${colors.border}`,
  background: "linear-gradient(180deg, rgba(15, 118, 110, 0.08) 0%, rgba(15, 23, 42, 0) 100%)",
};

const titleRowStyle = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: spacing.md,
};

const titleStyle = {
  margin: 0,
  color: colors.text,
  fontSize: typography.size.xl,
  fontWeight: typography.weight.bold,
  lineHeight: typography.lineHeight.compact,
};

const subtitleStyle = {
  margin: 0,
  color: colors.textSecondary,
  fontSize: typography.size.sm,
  lineHeight: typography.lineHeight.reading,
};

const closeButtonStyle = {
  minWidth: 40,
  minHeight: 40,
  padding: 0,
};

const badgeRowStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: spacing.sm,
};

const bodyStyle = {
  overflowY: "auto",
  padding: spacing.xl,
  display: "grid",
  gap: spacing.lg,
  alignContent: "start",
};

const sectionStyle = {
  display: "grid",
  gap: spacing.md,
  padding: spacing.lg,
  border: `1px solid ${colors.border}`,
  borderRadius: radius.xl,
  background: colors.surfaceSubtle,
};

const sectionTitleStyle = {
  margin: 0,
  color: colors.text,
  fontSize: typography.size.sm,
  fontWeight: typography.weight.bold,
};

const descriptionStyle = {
  margin: 0,
  color: colors.textSecondary,
  fontSize: typography.size.sm,
  lineHeight: typography.lineHeight.reading,
};

const actionRowStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: spacing.sm,
};

const detailGridStyle = {
  display: "grid",
  gap: spacing.md,
};

const detailRowStyle = {
  display: "grid",
  gap: spacing.xs,
  paddingBottom: spacing.sm,
  borderBottom: `1px solid ${colors.borderSubtle || colors.border}`,
};

const detailLabelStyle = {
  color: colors.textMuted,
  fontSize: typography.size.xs,
  fontWeight: typography.weight.bold,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
};

const detailValueStyle = {
  color: colors.text,
  fontSize: typography.size.sm,
  lineHeight: typography.lineHeight.reading,
  overflowWrap: "anywhere",
};

const getToneStyle = (tone) => {
  const resolved = toneTokens[tone] || toneTokens.neutral;
  return {
    background: resolved.background,
    borderColor: resolved.border,
    color: resolved.color,
  };
};

const formatLabel = (value) =>
  String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatDateTime = (value) => {
  if (!value) return "Not recorded";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not recorded";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const formatFileSize = (value) => {
  const size = Number(value);
  if (!Number.isFinite(size) || size < 0) return "Not recorded";
  if (size < 1024) return `${size} B`;
  if (size < 1024 ** 2) return `${(size / 1024).toFixed(1)} KB`;
  if (size < 1024 ** 3) return `${(size / 1024 ** 2).toFixed(1)} MB`;
  return `${(size / 1024 ** 3).toFixed(1)} GB`;
};

const getStorageTypeLabel = (value) => formatLabel(value) || "Record";
const getParentEntityTypeLabel = (value) => formatLabel(value) || "Record";
const getLifecycleLabel = (value) => (value ? formatLabel(value) : "Not set");

const getUploadedByLabel = (value) => {
  if (!value) return "Not recorded";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    return value.display_name || value.name || value.email || "Not recorded";
  }
  return "Not recorded";
};

const getTitle = (item) => item?.title || item?.filename || "Untitled record";

const getCaption = (item) => {
  if (item?.filename && item.filename !== item.title) return item.filename;
  if (item?.description) return item.description;
  return null;
};

const getStorageTone = (value) => {
  switch (value) {
    case "file":
      return "accent";
    case "url":
      return "info";
    case "generated":
      return "accent";
    default:
      return "neutral";
  }
};

const getLifecycleTone = (value) => {
  switch (String(value || "").trim().toLowerCase()) {
    case "active":
      return "accent";
    case "uploaded":
      return "info";
    case "approved":
    case "published":
      return "success";
    case "pending_upload":
    case "scanning":
      return "warning";
    case "quarantined":
      return "danger";
    default:
      return "neutral";
  }
};

const DetailRow = ({ label, value }) => (
  <div style={detailRowStyle}>
    <div style={detailLabelStyle}>{label}</div>
    <div style={detailValueStyle}>{value || "Not recorded"}</div>
  </div>
);

export default function SupportingEvidenceDetailDrawer({
  item,
  open,
  onClose,
  canViewDocument,
  onViewDocument,
  onDownload,
  onOpenLink,
  canOpenSource,
  onOpenSource,
}) {
  const titleId = useId();
  const closeRef = useRef(null);

  const detailRows = useMemo(
    () => [
      { label: "Source module", value: item?.source_module || "Not recorded" },
      { label: "Source type", value: formatLabel(item?.source_type) || "Not recorded" },
      { label: "Source record ID", value: item?.source_record_id != null ? String(item.source_record_id) : "Not recorded" },
      { label: "Parent entity", value: item?.parent_entity_title || "Not recorded" },
      { label: "Parent entity type", value: getParentEntityTypeLabel(item?.parent_entity_type) },
      { label: "Uploaded or created", value: formatDateTime(item?.created_at) },
      { label: "Last updated", value: formatDateTime(item?.updated_at) },
      { label: "Uploaded by", value: getUploadedByLabel(item?.uploaded_by) },
      { label: "Storage type", value: getStorageTypeLabel(item?.storage_type) },
      { label: "Lifecycle status", value: getLifecycleLabel(item?.lifecycle_status) },
      { label: "Scan status", value: formatLabel(item?.scan_status) || "Not recorded" },
      { label: "MIME type", value: item?.mime_type || "Not recorded" },
      { label: "File size", value: formatFileSize(item?.size_bytes) },
      { label: "Checksum", value: item?.checksum || "Not recorded" },
      { label: "Filename", value: item?.filename || "Not recorded" },
      { label: "Link", value: item?.url || "Not recorded" },
      { label: "Notes", value: item?.notes || "Not recorded" },
    ],
    [item]
  );

  if (!item) return null;

  const caption = getCaption(item);
  const description = item?.description && item.description !== caption ? item.description : null;

  return (
    <DrawerA11yWrapper
      open={open}
      onClose={onClose}
      labelledBy={titleId}
      initialFocusRef={closeRef}
      containerStyle={drawerStyle}
    >
      <div style={drawerLayoutStyle} data-testid="supporting-evidence-detail-drawer">
        <div style={headerStyle}>
          <div style={titleRowStyle}>
            <div style={{ display: "grid", gap: spacing.xs }}>
              <h2 id={titleId} style={titleStyle}>
                {getTitle(item)}
              </h2>
              {caption ? <p style={subtitleStyle}>{caption}</p> : null}
            </div>
            <Button
              type="button"
              variant="ghost"
              aria-label="Close supporting evidence details"
              onClick={onClose}
              ref={closeRef}
              style={closeButtonStyle}
            >
              X
            </Button>
          </div>
          <div style={badgeRowStyle}>
            <StatusBadge
              value="active"
              label={item?.source_module || "Unknown module"}
              style={getToneStyle("accent")}
            />
            <StatusBadge
              value="active"
              label={getStorageTypeLabel(item?.storage_type)}
              style={getToneStyle(getStorageTone(item?.storage_type))}
            />
            <StatusBadge
              value="active"
              label={getLifecycleLabel(item?.lifecycle_status)}
              style={getToneStyle(getLifecycleTone(item?.lifecycle_status))}
            />
          </div>
        </div>

        <div style={bodyStyle}>
          <section style={sectionStyle}>
            <h3 style={sectionTitleStyle}>Actions</h3>
            <p style={descriptionStyle}>Use the actions already available for this supporting evidence record.</p>
            <div style={actionRowStyle}>
              {canViewDocument ? (
                <Button type="button" variant="primary" onClick={onViewDocument}>
                  View document
                </Button>
              ) : null}
              {item?.can_download && item?.download_url ? (
                <Button type="button" variant="secondary" onClick={onDownload}>
                  Download
                </Button>
              ) : null}
              {item?.storage_type === "url" && item?.url ? (
                <Button type="button" variant="secondary" onClick={onOpenLink}>
                  Open link
                </Button>
              ) : null}
              {canOpenSource ? (
                <Button type="button" variant="ghost" onClick={onOpenSource}>
                  Open source
                </Button>
              ) : null}
            </div>
          </section>

          {description ? (
            <section style={sectionStyle}>
              <h3 style={sectionTitleStyle}>Description</h3>
              <p style={descriptionStyle}>{description}</p>
            </section>
          ) : null}

          {item?.associations?.length > 0 && <section style={sectionStyle}>
            <h3 style={sectionTitleStyle}>Associated records</h3>
            {item.associations.map((record) => <p key={`${record.target_type}:${record.target_id}`}>
              {record.target_type}: {record.title}{record.original ? " (original source)" : ""}
            </p>)}
          </section>}
          <section style={sectionStyle}>
            <h3 style={sectionTitleStyle}>Document details</h3>
            <div style={detailGridStyle}>
              {detailRows.map((row) => (
                <DetailRow key={row.label} label={row.label} value={row.value} />
              ))}
            </div>
          </section>
        </div>
      </div>
    </DrawerA11yWrapper>
  );
}
